import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import { or } from '@ember/object/computed';
import { task, timeout, restartableTask } from 'ember-concurrency';
import ProjectStaleModal from 'sh-www/components/modals/project/stale';
import ProjectNoLongerStaleModal from 'sh-www/components/modals/project/no-longer-stale';
import { showModal, closeModal } from 'sh-www/utils/modal-controls';
import { APPLICATION } from 'sh-www/models/application';
import { ACTION } from 'sh-www/models/action';
import { htmlSafe } from '@ember/template';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import ShowDeclineOtherApplicationsModalMixin from "sh-www/mixins/applications/show-decline-other-applications-modal-mixin";
import AssetUploadManager from 'sh-www/mixins/asset-upload-manager';
import {
  PROJECT_DETAILS__SUB_NAV_TITLES,
  APPLICATION_STATES_TO_SHOW_MARK_PROJECT_COMPLETE_REMINDER,
  PROJECT_DETAILS__REFRESH_TIMEOUT_MS,
  PROJECT_DETAILS__PROGRESSIVE_INCLUDES,
  PROJECT_DETAILS__BANNER_TYPES,
  getProjectDetailsPaymentIncludes,
  getProjectDetailsIncludes,
} from "sh-www/constants/routes/project-details";

const GLM_SOURCES = {
  STALE_WARNING_EMAIL: 'stale-warning-email',
};

class ProjectDetailsController extends Controller.extend(ShowDeclineOtherApplicationsModalMixin, AssetUploadManager) {
  @service('commands/activity-feed-item-commands') activityFeedItemCommands;
  @service('state-managers/application') applicationStateManager;
  @service('commands/application-commands') applicationCommandService;
  @service('commands/thread-commands') threadCommandService;
  @service('error-tracker') errorTracker;
  @service moment;
  @service session;
  @service store;

  queryParams = [
    'applicationAction',
    { glmSource: 'glm_source' },
  ];

  applicationAction = null;
  glmSource = null;

  application = null;
  applicationController = null;
  profileOverlay = null;
  profileOverlayType = null;
  profileOverlayModel = null;
  currentSubNavState = PROJECT_DETAILS__SUB_NAV_TITLES.ACTIVITY;
  incomingErrors = {};
  isShowingDeclineReminder = false;
  hideMarkCompleteReminder = false;
  hasUserClosedAboutToExpireReminder = false;
  hasTimeClosedAboutToExpireReminder = false;
  hasUploadsInActivityRoute = false;
  hasInitializedThreadMessages = false;
  initialMostRecentMessage = null;
  newActivityMessage = '';

  @or(
    'loadApplicationTask.isRunning',
    'loadPitchData.isRunning',
    'loadPitchDetails.isRunning',
    'loadPaymentData.isRunning',
    'loadThreadData.isRunning'
  ) isLoading;

  @computed('application.actions.@each.{createdAt,name}', 'application.seller.id')
  get sellerMarkedCompleteDate() {
    const sellerCompleteRequestActions = this.application.actions
      .filter(action => action.name === ACTION.NAMES.PROJECT_COMPLETE_REQUEST && action.user.id === this.application.seller.id);
    const mostRecentSellerCompleteRequestAction = sellerCompleteRequestActions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

    return mostRecentSellerCompleteRequestAction ? mostRecentSellerCompleteRequestAction.createdAt : null;
  }

  @computed('session.currentUser.isBuyer', 'sellerMarkedCompleteDate', 'application.status', 'hideMarkCompleteReminder')
  get isShowMarkCompleteReminder() {
    const now = this.moment.moment();
    return Boolean(
      !this.hideMarkCompleteReminder &&
      this.session.currentUser.isBuyer &&
      this.sellerMarkedCompleteDate &&
      APPLICATION_STATES_TO_SHOW_MARK_PROJECT_COMPLETE_REMINDER.includes(this.application.status) &&
      now.toDate().getTime() > this.sellerMarkedCompleteDate.getTime()
    )
  }

  @computed('isDecliningAllProposals', 'isShowMarkCompleteReminder', 'communicationExpireNotificationText', 'isShowingDeclineReminder')
  get isShowingBannerMessage() {
    return this.isDecliningAllProposals
      || this.isShowMarkCompleteReminder
      || this.communicationExpireNotificationText
      || this.isShowingDeclineReminder;
  }

  @computed('application.legalAgreements.@each.accepted')
  get isLegalSigningNeeded() {
    const legalAgreement = this.application.legalAgreements.objectAt(0);
    return !legalAgreement?.accepted;
  }

  @computed('application.legalAgreements.[]')
  get legalToShow() {
    const legalAgreement = this.application.legalAgreements.objectAt(0);
    return legalAgreement?.legal ?? null;
  }

  @computed('declineAllPendingApplications.isRunning', 'declineApplications.isRunning')
  get isDecliningAllProposals() {
    return this.declineAllPendingApplications.isRunning || this.declineApplications.isRunning;
  }

  @computed(
    'session.currentUser.isBuyer',
    'session.currentUser.{id,isBuyer}',
    'application.{createdAt,expiresAt}',
    'application.seller.{id,firstName}',
    'application.thread.messages.[]',
    'application.thread.messages.length',
    'hasTimeClosedAboutToExpireReminder',
    'hasUserClosedAboutToExpireReminder',
    'hasInitializedThreadMessages'
  )
  get communicationExpireNotificationText() {

    if (!this.session.currentUser.isBuyer) {
      return false;
    }

    const showNothing = this.hasTimeClosedAboutToExpireReminder  // timer has closed the reminder
      || !this.hasInitializedThreadMessages // haven't loaded thread, so do nothing
      || !this.application.expiresAt // this can not expire, so nothing to do
      || this.hasUserClosedAboutToExpireReminder; // the user has closed the reminder, so don't show anything.

    if (showNothing) {
      return '';
    }

    const messages = this.application.thread.messages;
    const buyerCompanyMessage = messages.find(message =>
      message.userId.toString() !== this.application.seller.id
      && !message.user.isAdmin
    )
    const isAnyMessageFromBuyerCompany = !!buyerCompanyMessage;

    if (isAnyMessageFromBuyerCompany) {
      return '';
    }

    const threeDaysFromNow = this.moment.moment().add(3, 'days');
    const expiresAt = this.moment.moment(this.application.expiresAt);

    if (expiresAt.toDate().getTime() < threeDaysFromNow.toDate().getTime()) {
      const createdAt = this.moment.moment(this.application.createdAt);
      const now = this.moment.moment();
      const daysSinceCreated = now.diff(createdAt, 'days');

      if (this.application.isExpiredTimeWithinOneDay) {
        return htmlSafe(`<span>You have not communicated with ${this.application.seller.firstName} for ${daysSinceCreated} days.</span><br><span class="inline-block margin-top-8px">Unless you respond to them, this application will be automatically declined in</span>`);
      }

      return htmlSafe(`<p class="text-center">You have not communicated with ${this.application.seller.firstName} for ${daysSinceCreated} days.</p><p class="text-center">Unless you respond to them, this application will be automatically declined in ${expiresAt.fromNow(true)}.</p>`);
    }

    return '';
  }

  @action
  setHasUploadsInActivityRoute(toggleBool) {
    this.set('hasUploadsInActivityRoute', toggleBool);
  }

  @action
  handleProfileOverlayClose() {
    this.applicationController.set('profileOverlay', null);
  }

  @action
  handleProfileLinkClick(profileOverlay, type, model) {
    const { currentUser } = this.session;
    const { application } = this;
    const { seller, company } = application;

    if (
      currentUser.isBuyer &&
      company.isNeedsCreditCard &&
      type === 'user' &&
      model.id === seller.id
    ) {
      showModal(this, 'payments/stripe-cc-modal', { application, seller });
      return;
    }

    this.setProperties({
      profileOverlayType: type,
      profileOverlayModel: model,
      'applicationController.profileOverlay': profileOverlay,
    });
  }

  @action
  handleMarkActiveClicked(application) {
    this.showModalWithLegal(application, () => {
      const isBuyerViewing = this.session.currentUser.isBuyer;
      const showFeedbackModal = (isBuyerViewing && application.status === APPLICATION.STATUS.COMPLETE_PRODUCER)
        || (!isBuyerViewing && application.status === APPLICATION.STATUS.COMPLETE_PUBLISHER);

      if (showFeedbackModal) {
        const modalName = 'projects/mark-project-active-feedback';
        const companyName = application.company.name;
        const bodyText = isBuyerViewing
          ? 'Mark this project active if there is further work to be completed.'
          : `Keep this project open to keep collaborating with ${companyName}.`;

        const modalModel = {
          application,
          bodyText,
          isBuyerViewing,
          submitAction: (application, feedback) => {
            return this.markApplicationActive.perform(application, feedback);
          }
        };

        showModal(this, modalName, modalModel);
      } else {
        const modalName = 'confirmation';
        const notifierName = isBuyerViewing ? application.seller.firstName : application.buyer.firstName;

        const modalModel = {
          titleText: 'Mark project active',
          bodyText: `If this project is not yet complete, you can mark it active. The project will become active again and ${notifierName} will be notified.`,
          actionButtonText: 'Mark active',
          isShowCancelButton: true,
          confirmAction: () => {
            this.markApplicationActive.perform(application);
          }
        };

        showModal(this, modalName, modalModel);
      }
    });
  }

  @task
  *markApplicationActive(application, opt_feedback) {
    try {
      yield this.applicationCommandService.withdrawCompleteRequest(application, opt_feedback);
      this.applicationStateManager.addToast('Project marked active');

      // this will both reload the application as well as load the latest thread state
      yield this.loadThreadData.perform(application);
    } catch (e) {
      handleErrorResponseWithModal(this, e, 'There was an error marking the application active. Please refresh and try again.');
    }
  }

  showModalWithLegal(application, modalCallback) {
    if (this.session.currentUser.isSeller && this.isLegalSigningNeeded) {
      const { legalToShow } = this;
      const legalModalModel = {
        application,
        legalToShow,
        onLegalAgreed: () => {
          this.applicationCommandService.agreeToLegal(application).then(() => {
            this.loadApplicationTask.perform(application.id);
            modalCallback();
          });
        }
      };

      showModal(this, 'projects/accept-terms-modal', legalModalModel);
    } else {
      modalCallback();
    }
  }

  @task
  *refreshApplicationTask({ id }) {
    const includes = [
      ...getProjectDetailsPaymentIncludes(this.session.currentUser),
      ...PROJECT_DETAILS__PROGRESSIVE_INCLUDES.THREAD,
      ...PROJECT_DETAILS__PROGRESSIVE_INCLUDES.PITCH,
      ...getProjectDetailsIncludes(this.session.currentUser),
    ]

    while (true) {
      yield timeout(PROJECT_DETAILS__REFRESH_TIMEOUT_MS);
      try {
        const application = yield this.silentlyLoadApplication(id, includes);
        this.set('application', application);
      } catch (e) {
        this.errorTracker.error(e, this, `refreshApplicationTask failed for application id ${id}.`);
      }
    }
  }

  @action
  async silentlyLoadApplication(applicationIdOrSlug, includes = []) {
    const { currentUser } = this.session;
    const finalIncludes = [...includes];

    if (currentUser.isBuyer && currentUser.company?.id) {
      finalIncludes.push(`seller.company-seller-connections:in_network_or_extended(${currentUser.company.id}).company`);
    }

    try {
      const response = await this.applicationCommandService.loadApplication(applicationIdOrSlug, finalIncludes);
      this.store.pushPayload(response);
      const application = this.store.peekRecord('application', response.data.id);
      return application;
    } catch(e) {
      this.errorTracker.error(e, this, `silentlyLoadApplication task failed for ${applicationIdOrSlug}.`);
      throw e;
    }
  }

  @restartableTask
  *loadApplicationTask(applicationIdOrSlug, includes = []) {
    const application = yield this.silentlyLoadApplication(applicationIdOrSlug, includes);
    this.set('application', application);
  }

  @restartableTask
  *loadPitchData({ id }) {
    const application = yield this.silentlyLoadApplication(id, PROJECT_DETAILS__PROGRESSIVE_INCLUDES.PITCH);
    this.set('application', application);
  }

  @restartableTask
  *loadPaymentData({ id }) {
    const application = yield this.silentlyLoadApplication(id, getProjectDetailsPaymentIncludes(this.session.currentUser));
    this.set('application', application);
  }

  @restartableTask
  *loadFilesData({ id }) {
    const application = yield this.silentlyLoadApplication(id, PROJECT_DETAILS__PROGRESSIVE_INCLUDES.FILES);
    this.set('application', application);
  }

  @restartableTask
  *loadThreadData({ id }) {
    const application = yield this.silentlyLoadApplication(id, PROJECT_DETAILS__PROGRESSIVE_INCLUDES.THREAD);
    this.setProperties({
      application,
      initialMostRecentMessage: application.thread.messages.firstObject?.id ?? null,
      hasInitializedThreadMessages: true
    });
  }

  @action
  declineOtherApplicationsModal(application) {
    this.showDeclineOtherApplicationsModal(application.assignment, application.seller).then(() => {
      this.applicationStateManager.addToast('Other applications declined');
    }).catch((e) => {
      this.applicationStateManager.addToast('An error occurred while declining other applications. Please refresh and try again.');
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error declining other applications. Please refresh and try again.',
        'declineOtherApplicationsModal -- error app id' + application.id
      );
    });
  }

  @task
  *addAssetFromLibrary(application, asset) {
    try {
      yield this.applicationCommandService.attachAssetFromLibrary(application, asset);
      this.applicationStateManager.addToast('File added');
    } catch (e) {
      this.applicationStateManager.addToast('There was a problem adding file. Please try again.');
      this.errorTracker.error('addAssetFromLibrary - ' + application.id, this, e);
    }
  }

  @task
  *approveOffer(application) {
    try {
      yield this.applicationCommandService.approveOffer(application.id);

      this.session.currentUser.isBuyer
        ? this.applicationStateManager.addToast('Offer accepted')
        : this.applicationStateManager.addToast('Project started');

      // this will both reload the application as well as load the latest thread state
      yield this.loadThreadData.perform(application);
    } catch(e) {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error approving the project offer. Please refresh and try again.',
        'approveOffer -- error approving project offer app id: ' + application.id
      );
    }
  }

  @task
  *agreeToLegal(application) {
    try {
      yield this.applicationCommandService.agreeToLegal(application);
      yield this.loadApplicationTask.perform(application.id);
    } catch (e) {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error attempting to accept legal agreement. Please refresh and try again.',
        'agreeToLegal -- error accepting legal app id: ' + application.id
      );
    }
  }

  @task
  *declineAllPendingApplications() {
    try {
      const assignment = yield this.assignmentCommandService.loadAssignment(this.application.assignment.id, ['applications']);
      const applicationIds = assignment.applications.filter(application => {
        return APPLICATION.SIMPLE_STATUS_MAPPING[APPLICATION.SIMPLE_STATUS.PENDING].includes(application.status);
      }).map(({ id }) => id);

      const modalModel = {
        title: `Decline pending proposals`,
        textAreaPlaceholder: `Optional: Leave a note.`,
        cancelText: 'Cancel',
        confirmText: 'Decline',
        secondaryMessage: `This decline message will be sent to ${applicationIds.length} freelancers`,
        onConfirmMessage: (message) => {
          this.declineApplications.perform(applicationIds, message);
        }
      };
      showModal(this, 'lsg/lsg-modal-text-editor', modalModel);
    } catch (e) {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error declining the applications. Please refresh and try again.',
        'declineAllPendingApplications -- error loading assignment id: ' + this.application.assignment.id
      );
    }
  }

  @task
  *declineApplications(applicationIds, message) {
    try {
      yield this.applicationCommandService.declineApplications(applicationIds, message);
      this.setReminderBannerDismissed(PROJECT_DETAILS__BANNER_TYPES.DECLINE_REMINDER);
    } catch (e) {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error declining the applications. Please refresh and try again.',
        'onDeclineAllPendingApplicationsClicked -- error declining apps for assign id: ' + this.application.assignment.id
      );
    }
  }

  @action
  setReminderBannerDismissed(bannerTypeClosed) {
    switch (bannerTypeClosed) {
      case PROJECT_DETAILS__BANNER_TYPES.DECLINE_REMINDER:
        this.set('isShowingDeclineReminder', false);
        break;
      case PROJECT_DETAILS__BANNER_TYPES.ABOUT_TO_EXPIRE:
        this.set('hasUserClosedAboutToExpireReminder', true);
        break;
      case PROJECT_DETAILS__BANNER_TYPES.MARK_COMPLETE_REMINDER:
        this.set('hideMarkCompleteReminder', false);
        break;
    }
  }

  @action
  onAssetFinalized(uploadID) {
    const { asset, filename } = this.uploads[uploadID];

    this.applicationCommandService.attachAssetToApplication(this.application, asset).then(() => {
      this.applicationStateManager.addToast(`Added ${filename}`);
    })
    .catch(() => {
      this.applicationStateManager.addToast(`Failed to add ${filename}`);
    })
    .finally(() => {
      this.removeFile(uploadID);
    });
  }

  @action
  onFileInputChange(files) {
    this.addFiles(files);
  }

  @action
  onClickDeleteAssetButton(uploadID) {
    this.removeFile(uploadID);
  }

  @action
  onAddAssetFromLibrary() {
    const modalData = {
      company: this.store.peekRecord('company', this.application.company.id),
      currentUser: this.session.currentUser,
      onAssetAdded: (asset) => {
        this.addAssetFromLibrary.perform(this.application, asset);
        closeModal(this);
      }
    };
    showModal(this, 'projects/add-asset-from-library-modal', modalData);
  }

  @action
  updateSubNavState(subNavState) {
    this.set('currentSubNavState', subNavState);
  }

  @action
  handleApplicationExpired() {
    this.set('hasTimeClosedAboutToExpireReminder', true);
  }

  @action
  handleApplicationAction() {
    const hasCompleteStatus = [APPLICATION.STATUS.COMPLETE_PRODUCER, APPLICATION.STATUS.COMPLETE_PUBLISHER].includes(this.application.status);
    const hasMarkActiveParam = this.applicationAction === 'markActive';

    if (hasCompleteStatus && hasMarkActiveParam) {
      this.handleMarkActiveClicked(this.application);
    }
  }

  @action
  handleViewStaleApplication() {
    if (this.session.currentUser.isAdmin) {
      return;
    }

    const { application, loadPaymentData, loadThreadData } = this;

    // viewing from email saying action needed but action already taken (potentially by other party)
    if (application.isInRemediation) {
      showModal(this, ProjectStaleModal, {
        application,
        reloadApplicationCallback: () => {
          loadPaymentData.perform(application);
          loadThreadData.perform(application);
        },
      });
    } else if (this.glmSource === GLM_SOURCES.STALE_WARNING_EMAIL) {
      showModal(this, ProjectNoLongerStaleModal, {
        application,
      });
    }

    this.set('glmSource', null);
  }

  resetProperties() {
    this.setProperties({
      applicationAction: null,
      glmSource: null,
      profileOverlay: null,
      profileOverlayType: null,
      profileOverlayModel: null,
      incomingErrors: {},
      isShowingDeclineReminder: false,
      hideMarkCompleteReminder: false,
      hasUserClosedAboutToExpireReminder: false,
      hasTimeClosedAboutToExpireReminder: false,
      hasUploadsInActivityRoute: false,
      hasInitializedThreadMessages: false,
      initialMostRecentMessage: null,
      newActivityMessage: '',
    });
  }

  @action
  markViewed(application) {
    this.activityFeedItemCommands.markRecentProjectItemRead(application);
  }
}

export default ProjectDetailsController;
